let activeReactErrorHandler
let loadPromise
const pendingReactErrors = []
const pendingWindowErrors = []

const queueWindowError = (event) => pendingWindowErrors.push(event.error || new Error(event.message))
const queueUnhandledRejection = (event) => {
  // Recovery listeners (notably stale Inertia bundle recovery) call preventDefault later in the
  // same dispatch. Wait until all listeners ran so successfully recovered rejections stay out of
  // Sentry instead of being reported when deferred monitoring eventually loads.
  window.queueMicrotask(() => {
    if (event.defaultPrevented) return
    pendingWindowErrors.push(event.reason instanceof Error ? event.reason : new Error(String(event.reason)))
  })
}

if (import.meta.env.PROD && typeof window !== 'undefined') {
  window.addEventListener('error', queueWindowError)
  window.addEventListener('unhandledrejection', queueUnhandledRejection)
}

function loadSentry() {
  if (!import.meta.env.PROD) return Promise.resolve()
  if (loadPromise) return loadPromise

  loadPromise = import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: 'https://ee73df29657c6f55b924bf0e8e696a30@o4511729368301568.ingest.us.sentry.io/4511729454415877',
      environment: import.meta.env.MODE,
      sendDefaultPii: false,
      // Cloudflare injects its RUM beacon into every page; its internal errors are not ours to fix.
      denyUrls: [/\/beacon\.min\.js/],
      // Mobile in-app browsers inject brittle native bridge shims into otherwise ordinary pages.
      // These exact failures originate in those shims, not in Tinker's postMessage bridge.
      ignoreErrors: [
        /Error invoking postMessage: Java object is gone/,
        /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers'\)/,
        // The admin comparison page intentionally polls while long generation jobs run. A lost
        // poll is retried two seconds later and does not affect the comparison itself.
        /Network error \(https:\/\/www\.tinker\.fun\/admin\/model-comparisons\/\d+\)/,
      ],
    })
    activeReactErrorHandler = Sentry.reactErrorHandler()
    window.removeEventListener('error', queueWindowError)
    window.removeEventListener('unhandledrejection', queueUnhandledRejection)
    pendingWindowErrors.splice(0).forEach((error) => Sentry.captureException(error))
    pendingReactErrors.splice(0).forEach((args) => activeReactErrorHandler(...args))
  })

  return loadPromise
}

export function reactErrorHandler(...args) {
  if (activeReactErrorHandler) activeReactErrorHandler(...args)
  else pendingReactErrors.push(args)
}

export function scheduleSentry() {
  if (!import.meta.env.PROD) return

  const start = () => {
    window.removeEventListener('pointerdown', start)
    window.removeEventListener('keydown', start)
    loadSentry()
  }
  window.addEventListener('pointerdown', start, { once: true, passive: true })
  window.addEventListener('keydown', start, { once: true })
  window.addEventListener('load', () => window.setTimeout(start, 15_000), { once: true })
}
